import type React from 'react';
import { flushSync } from 'react-dom';
import { useCallback, useRef, useEffect, useState, useMemo } from 'react';
import {
    type NumberBlurHandler,
    type NumberChangeHandler,
    type NumberFocusHandler,
} from '@splunk/react-ui/Number';
import { debounce } from 'lodash';
import useEventCallback from './useEventCallback';

export type OnValueChangeType = (
    e: Parameters<NumberChangeHandler>[0],
    newValue?: string | number
) => void;

interface UseNumberInputArgs {
    value?: string | number;
    defaultValue?: string | number;
    onValueChange: OnValueChangeType;
}

export const useNumberInput = ({
    value,
    defaultValue,
    onValueChange,
}: UseNumberInputArgs) => {
    const [currentValue, setCurrentValue] = useState(value);
    const isFocusedRef = useRef(false);

    const commitValue = useCallback(
        (e: Parameters<NumberChangeHandler>[0], newValue?: string | number) => {
            if (newValue == null) {
                // if we're clearing the input, revert local state back to default or old value
                setCurrentValue(defaultValue ?? value);
            }

            onValueChange(e, newValue);
        },
        [defaultValue, onValueChange, value]
    );

    const handleChangeDebounced = useMemo(
        () =>
            debounce<NumberChangeHandler>(
                (e, payload) => commitValue(e, payload.value),
                250
            ),
        [commitValue]
    );

    useEffect(() => {
        setCurrentValue(value);
    }, [value]);

    const handleChange = useEventCallback<NumberChangeHandler>((e, payload) => {
        setCurrentValue(payload.value);
        if (isFocusedRef.current) {
            // if focus is in the text box of the number input, don't commit the state yet
            // this includes being focused inside the text box and using UP + DOWN arrow keys to increment/decrement. It will only
            //   commit the value on blur or Enter key.
            return;
        }

        // if the focus is outside, it's because the user is clicking the increment/decrement buttons directly
        handleChangeDebounced(e, payload);
    });

    const handleBlur = useEventCallback<NumberBlurHandler>((e) => {
        isFocusedRef.current = false;

        if (
            value !== currentValue &&
            value !== Number.parseInt(e.currentTarget?.value, 10)
        ) {
            commitValue(e, currentValue);
        }
    });

    const handleFocus = useCallback<NumberFocusHandler>(() => {
        isFocusedRef.current = true;
    }, []);

    const handleKeyPress = useEventCallback<
        React.KeyboardEventHandler<HTMLInputElement>
    >((e) => {
        if (typeof e.key === 'string' && e.key.toLowerCase() === 'enter') {
            if (
                value !== currentValue &&
                value !== Number.parseInt(e.currentTarget?.value, 10)
            ) {
                commitValue(e, currentValue);
            }
        }
        // if pressing the ESC key, revert value to the initial one before editing
        if (typeof e.key === 'string' && e.key.toLowerCase() === 'escape') {
            // This key doesn't work with Chrome 75
            flushSync(() => {
                setCurrentValue(value);
            });
        }
    });

    return {
        handleChange,
        handleBlur,
        handleFocus,
        handleKeyPress,
        currentValue,
    };
};
